import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isRegistered } from './breadcrumbs'

/**
 * 라우트.
 *
 * `meta.requires` 는 필요한 최소 권한이다. 가드가 막지만 **서버도 막는다** —
 * 프론트 가드는 UX 이고 권한의 근거는 API 다 (docs/10).
 */
type Requirement = 'employee' | 'manager' | 'admin'

declare module 'vue-router' {
  interface RouteMeta {
    requires?: Requirement
    /** 로그인하지 않아도 되는 화면 */
    public?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/components/shared/AppShell.vue'),
    children: [
      { path: '', redirect: { name: 'dashboard' } },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: 'my-attendance',
        name: 'my-attendance',
        component: () => import('@/views/MyAttendanceView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: 'my-summary',
        name: 'my-summary',
        component: () => import('@/views/MySummaryView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/views/NotificationsView.vue'),
        meta: { requires: 'employee' },
      },

      {
        path: 'corrections',
        name: 'corrections-mine',
        component: () => import('@/views/correction/MyCorrectionsView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: 'corrections/new',
        name: 'correction-new',
        component: () => import('@/views/correction/CorrectionFormView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: 'corrections/:id(\\d+)',
        name: 'correction-detail',
        component: () => import('@/views/correction/CorrectionDetailView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: 'approvals',
        name: 'correction-inbox',
        component: () => import('@/views/correction/CorrectionInboxView.vue'),
        meta: { requires: 'manager' },
      },

      {
        path: 'team/today',
        name: 'team-today',
        component: () => import('@/views/team/TeamTodayView.vue'),
        meta: { requires: 'manager' },
      },
      {
        path: 'team/employees/:id(\\d+)',
        name: 'team-member',
        component: () => import('@/views/team/TeamMemberView.vue'),
        meta: { requires: 'manager' },
      },

      {
        path: 'reports/period',
        name: 'report-period',
        component: () => import('@/views/report/PeriodReportView.vue'),
        meta: { requires: 'manager' },
      },
      {
        path: 'reports/employee/:id(\\d+)',
        name: 'report-employee',
        component: () => import('@/views/report/EmployeeDetailReportView.vue'),
        meta: { requires: 'manager' },
      },
      {
        path: 'reports/work-sites',
        name: 'report-site',
        component: () => import('@/views/report/SiteSummaryView.vue'),
        meta: { requires: 'manager' },
      },
      {
        path: 'reports/payroll',
        name: 'report-payroll',
        component: () => import('@/views/report/PayrollReportView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'reports/anomalies',
        name: 'report-anomalies',
        component: () => import('@/views/report/AnomalyView.vue'),
        meta: { requires: 'manager' },
      },
      {
        path: 'reports/exports',
        name: 'export-jobs',
        component: () => import('@/views/report/ExportJobsView.vue'),
        meta: { requires: 'admin' },
      },

      {
        path: 'admin/time-entries',
        name: 'admin-entries',
        component: () => import('@/views/admin/AdminEntriesView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/time-entries/:id(\\d+)',
        name: 'admin-entry-detail',
        component: () => import('@/views/admin/AdminEntryDetailView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/employees',
        name: 'admin-employees',
        component: () => import('@/views/admin/EmployeesView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/employees/import',
        name: 'admin-employee-import',
        component: () => import('@/views/admin/EmployeeImportView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/employees/:id(\\d+)',
        name: 'admin-employee-detail',
        component: () => import('@/views/admin/EmployeeDetailView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/work-sites',
        name: 'admin-work-sites',
        component: () => import('@/views/admin/WorkSitesView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/work-sites/:id(\\d+)',
        name: 'admin-work-site-detail',
        component: () => import('@/views/admin/WorkSiteDetailView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/holidays',
        name: 'admin-holidays',
        component: () => import('@/views/admin/HolidaysView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/break-waivers',
        name: 'admin-break-waivers',
        component: () => import('@/views/admin/BreakWaiversView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/monitored',
        name: 'admin-monitored',
        component: () => import('@/views/admin/MonitoredView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/gps-failures',
        name: 'admin-gps-failures',
        component: () => import('@/views/admin/GpsFailuresView.vue'),
        meta: { requires: 'admin' },
      },
      {
        path: 'admin/audit-logs',
        name: 'admin-audit',
        component: () => import('@/views/admin/AuditLogView.vue'),
        meta: { requires: 'admin' },
      },

      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenView.vue'),
        meta: { requires: 'employee' },
      },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue'),
        meta: { requires: 'employee' },
      },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

function satisfies(requirement: Requirement | undefined, roles: string[]): boolean {
  switch (requirement) {
    case 'admin':
      return roles.includes('ROLE_ADMIN')
    case 'manager':
      return roles.includes('ROLE_MANAGER')
    case 'employee':
      return roles.includes('ROLE_EMPLOYEE')
    default:
      return true
  }
}

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // 새로고침 직후엔 액세스 토큰이 메모리에 없다. 한 번 복구를 시도한다.
  if (auth.booting) {
    await auth.restore()
  }

  if (true === to.meta.public) {
    // 이미 로그인했으면 로그인 화면에 머무르지 않는다.
    return auth.isAuthenticated ? { name: 'dashboard' } : true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (!satisfies(to.meta.requires, auth.roles)) {
    return { name: 'forbidden' }
  }

  /*
   * 브레드크럼 등록 확인. 등록하지 않은 라우트로 가면 대시보드로 돌린다 —
   * 화면 상단이 비어 사용자가 길을 잃는 것보다 낫다. 다만 **왜** 튕겼는지
   * 콘솔에 남긴다. 이 원인을 찾는 데 시간을 쓴 적이 있다.
   */
  const name = 'string' === typeof to.name ? to.name : ''
  if ('' !== name && !isRegistered(name)) {
    if (import.meta.env.DEV) {
      console.error(
        `[router] route "${name}" is not registered in router/breadcrumbs.ts — redirecting to dashboard.`,
      )
    }

    return { name: 'dashboard' }
  }

  return true
})
